import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaXmark,
    FaBars,
} from "react-icons/fa6";
import {
    AiOutlineDashboard,
    AiOutlineRobot,
    AiOutlineUser,
    AiOutlineBook,
    AiFillHome,
} from "react-icons/ai"
import "./MSidebar.css";

// 选项映射到索引
const OPTION_INDEX_MAP = {
    "仪表盘": 0,
    "假人控制": 1,
    "个人主页": 2,
    "资料库": 3,
    "返回主页": 4
};

// 索引映射到选项
const INDEX_OPTION_MAP = [
    "仪表盘",
    "假人控制",
    "个人主页",
    "资料库",
    "返回主页"
];

const MSidebar = ({ selectedIndex: externalSelectedIndex = 0, onOptionChange }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [selected, setSelected] = useState(INDEX_OPTION_MAP[externalSelectedIndex] || "仪表盘");
  const [isHovering, setIsHovering] = useState(false);
  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);
  const optionRefs = useRef([]);
  const navigate = useNavigate()

  // 检测是否为移动设备（竖屏）
  const [isMobile, setIsMobile] = useState(false);

  // 当外部传入的selectedIndex变化时，更新内部选中状态
  useEffect(() => {
    if (externalSelectedIndex >= 0 && externalSelectedIndex < INDEX_OPTION_MAP.length) {
      const title = INDEX_OPTION_MAP[externalSelectedIndex];
      if (title && title !== selected) {
        setSelected(title);
      }
    }
  }, [externalSelectedIndex, selected]);

  useEffect(() => {
    const checkMobile = () => {
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      setIsMobile(isPortrait);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    window.addEventListener("orientationchange", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("orientationchange", checkMobile);
    };
  }, []);

  // 更新遮罩位置
  const updateOverlayPosition = useCallback(() => {
    if (!isMobile && overlayRef.current) {
      const options = [
        { title: "仪表盘", index: 0 },
        { title: "假人控制", index: 1 },
        { title: "个人主页", index: 2 },
        { title: "资料库", index: 3 },
        { title: "返回主页", index: 4 }
      ];
      
      const selectedOption = options.find(opt => opt.title === selected);
      if (selectedOption && optionRefs.current[selectedOption.index]) {
        const optionElement = optionRefs.current[selectedOption.index];
        const containerRect = optionElement.parentElement.getBoundingClientRect();
        const optionRect = optionElement.getBoundingClientRect();
        
        overlayRef.current.style.transform = `translateY(${optionRect.top - containerRect.top}px)`;
      }
    }
  }, [selected, isMobile]);

  // 桌面端：鼠标悬停时展开，离开时收起
  useEffect(() => {
    if (!isMobile && sidebarRef.current) {
      const sidebar = sidebarRef.current;

      const handleMouseEnter = () => {
        setIsHovering(true);
        // 延迟更新遮罩位置，等待展开动画完成
        setTimeout(updateOverlayPosition, 300);
      };

      const handleMouseLeave = () => {
        setIsHovering(false);
        // 延迟更新遮罩位置，等待收起动画完成
        setTimeout(updateOverlayPosition, 300);
      };

      sidebar.addEventListener("mouseenter", handleMouseEnter);
      sidebar.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        sidebar.removeEventListener("mouseenter", handleMouseEnter);
        sidebar.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [isMobile, updateOverlayPosition]);

  // 当选中的选项改变时更新遮罩位置
  useEffect(() => {
    updateOverlayPosition();
  }, [selected, updateOverlayPosition]);

  // 点击页面其他区域关闭移动端侧边栏
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileOpen &&
        !event.target.closest(".m-sidebar-mobile") &&
        !event.target.closest(".m-hamburger")
      ) {
        setIsMobileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileOpen]);

  // 防止滚动当移动端侧边栏打开时
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobileOpen]);

  // 处理选项点击
  const handleOptionClick = (title) => {
    const index = OPTION_INDEX_MAP[title];
    
    // 更新内部状态
    setSelected(title);
    
    // 如果父组件传入了回调函数，调用它并传递index
    if (onOptionChange && typeof onOptionChange === 'function') {
      onOptionChange(index);
    }
    
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  // 处理返回主页
  const handleHomeClick = () => {
    const index = OPTION_INDEX_MAP["返回主页"];
    
    // 更新内部状态
    setSelected("返回主页");
    
    // 如果父组件传入了回调函数，调用它并传递index
    if (onOptionChange && typeof onOptionChange === 'function') {
      onOptionChange(index);
    }
    
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  return (
    <>
      {/* 桌面端侧边栏 */}
      {!isMobile && (
        <nav
          ref={sidebarRef}
          className="m-sidebar-desktop"
          data-expanded={isHovering}
        >
          <div className="m-sidebar-inner">
            <div className="m-title-section">
              <div className="m-title-container">
                <div className="m-title-content">
                  <Logo m={isMobile}/>
                  {isHovering && (
                    <div className="m-title-text-container">
                      <h2 className="m-title-text">控制面板</h2>
                      <p className="m-subtitle-text">CONTROL PANEL</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="m-options-container">
              {/* 桌面端遮罩 - 只有一个 */}
              <div className="m-selection-overlay" ref={overlayRef} />
              
              <OptionDesktop
                ref={el => optionRefs.current[0] = el}
                Icon={AiOutlineDashboard}
                title="仪表盘"
                selected={selected}
                onClick={handleOptionClick}
                isExpanded={isHovering}
              />
              <OptionDesktop
                ref={el => optionRefs.current[1] = el}
                Icon={AiOutlineRobot}
                title="假人控制"
                selected={selected}
                onClick={handleOptionClick}
                isExpanded={isHovering}
              />
              <OptionDesktop
                ref={el => optionRefs.current[2] = el}
                Icon={AiOutlineUser}
                title="个人主页"
                selected={selected}
                onClick={handleOptionClick}
                isExpanded={isHovering}
              />
              <OptionDesktop
                ref={el => optionRefs.current[3] = el}
                Icon={AiOutlineBook}
                title="资料库"
                selected={selected}
                onClick={handleOptionClick}
                isExpanded={isHovering}
              />
            </div>
            
            <div className="m-sidebar-footer">
              <a href="https://tdy.ororigin.xyz" style={{textDecoration:"none"}}>
              <OptionDesktop
                ref={el => optionRefs.current[4] = el}
                Icon={AiFillHome}
                title="返回主页"
                selected={selected}
                onClick={handleHomeClick}
                isExpanded={isHovering}
              />
              </a>
            </div>
          </div>
        </nav>
      )}

      {/* 移动端顶部栏 */}
      {isMobile && (
        <div className="m-topbar">
          <div className="m-topbar-content">
            <Logo m={isMobile}/>
            <div className="m-topbar-title">
              <h2 className="m-title-text">控制面板</h2>
              <p className="m-subtitle-text">CONTROL PANEL</p>
            </div>
            <button
              className="m-hamburger"
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              aria-label="菜单"
            >
              <FaBars />
            </button>
          </div>
        </div>
      )}

      {/* 移动端侧边栏遮罩 */}
      {isMobile && isMobileOpen && (
        <div
          className="m-mobile-overlay"
          onClick={() => setIsMobileOpen(false)}
          data-open={isMobileOpen}
        />
      )}

      {/* 移动端侧边栏 */}
      {isMobile && (
        <div className="m-sidebar-mobile" data-open={isMobileOpen}>
          <div className="m-mobile-header">
            <div className="m-mobile-title">
              <Logo m={isMobile}/>
              <div className="m-mobile-title-text">
                <h2 className="m-title-text">控制面板</h2>
                <p className="m-subtitle-text">CONTROL PANEL</p>
              </div>
            </div>
            <button
              className="m-mobile-close"
              onClick={() => setIsMobileOpen(false)}
              aria-label="关闭菜单"
            >
              <FaXmark />
            </button>
          </div>

          <div className="m-options-container">
            <OptionMobile
              Icon={AiOutlineDashboard}
              title="仪表盘"
              selected={selected}
              onClick={handleOptionClick}
            />
            <OptionMobile
              Icon={AiOutlineRobot}
              title="假人控制"
              selected={selected}
              onClick={handleOptionClick}
            />
            <OptionMobile
              Icon={AiOutlineUser}
              title="个人主页"
              selected={selected}
              onClick={handleOptionClick}
            />
            <OptionMobile
              Icon={AiOutlineBook}
              title="资料库"
              selected={selected}
              onClick={handleOptionClick}
            />
          </div>

          <div className="m-mobile-footer">
            <OptionMobile
              Icon={AiFillHome}
              title="返回主页"
              selected={selected}
              onClick={handleHomeClick}
            />
          </div>
        </div>
      )}
    </>
  );
};

// 桌面端选项组件 - 使用div而不是button
const OptionDesktop = React.forwardRef(({
  Icon,
  title,
  selected,
  onClick,
  isExpanded,
}, ref) => {
  const isSelected = selected === title;
  
  return (
    <div
      ref={ref}
      className={`m-option-desktop ${isSelected ? "selected" : ""}`}
      onClick={() => onClick(title)}
      aria-label={title}
    >
      <div className="m-option-icon">
        <Icon />
      </div>
      {isExpanded && <span className="m-option-text">{title}</span>}
    </div>
  );
});

// 移动端选项组件 - 使用button
const OptionMobile = ({
  Icon,
  title,
  selected,
  onClick,
}) => {
  const isSelected = selected === title;
  
  return (
    <button
      className={`m-option-mobile ${isSelected ? "selected" : ""}`}
      onClick={() => onClick(title)}
      aria-label={title}
    >
      <div className="m-option-icon">
        <Icon />
      </div>
      <span className="m-option-text">{title}</span>
    </button>
  );
};

const Logo = (props) => {
  return (
    <div className="m-logo" style={{flexDirection:props.m ? "row" : "column" , marginRight: props.m && "1rem"}}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        className="m-logo-icon"
        style={{height:props.m ? "4rem" : "3.5rem"}}
      >
        <defs>
          <style>
            {`
              .st0, .st1, .st2, .st3 {
                stroke: #ffefd5;
                stroke-miterlimit: 10;
              }
              .st0, .st3 {
                fill: none;
              }
              .st1 {
                fill: #dfb978;
              }
              .st2{
                fill: #ffefd5;
              }
              .st3 {
                stroke-linecap: round;
              }
            `}
          </style>
        </defs>
        <path className="st1" d="M-11.25-44.03" />
        <g>
          <path className="st0" d="M13.96,1.26" />
          <polyline
            className="st0"
            points="7.85 13.9 13.42 11.82 13.42 5.49 8.06 7.46"
          />
          <polyline className="st0" points="2.77 7.76 2.77 5.64 7.9 7.5" />
          <line className="st0" x1="2.77" y1="8.59" x2="2.77" y2="7.51" />
          <line className="st0" x1="2.77" y1="10.06" x2="2.77" y2="8.59" />
          <polyline className="st0" points="8.21 13.9 2.77 11.75 2.77 10.06" />
          <line className="st3" x1="8.12" y1="7.51" x2="8.12" y2="13.8" />
          <line className="st3" x1="8.04" y1="2.71" x2="13.42" y2="4.83" />
          <line className="st3" x1="7.94" y1="2.68" x2="2.76" y2="4.97" />
          <polygon
            className="st2"
            points="8.54 13.46 13.3 11.56 13.24 5.56 8.44 7.44 8.54 13.46"
          />
          <polygon
            className="st2"
            points="4.33 10.49 6.36 11.31 6.39 8.58 4.3 7.79 4.33 10.49"
          />
        </g>
      </svg>
      <text style={{color:"#ffefd5" , fontSize:props.m && "1.75rem"}}>天地源</text>
    </div>
  );
};

export default MSidebar;